/**
 * Local replacement for `styled` from `enova-frontend-components`.
 *
 * A thin wrapper around styled-components that pre-binds the theme type, so
 * template literals keep full `theme.*` typing without every consumer having
 * to declare it. It exists so the frontend projects can drop
 * `enova-frontend-components` one piece at a time -- `styled` is by far its
 * most widely used export, and it has nothing to do with efc's components.
 *
 * The theme type still comes from efc, because the theme object itself is
 * still supplied by efc's `EnovaContextProvider`. When the theme moves out of
 * efc, this file is the only place that needs to change.
 *
 * Deliberately duplicated from `ems-common` (Ems/Client/Common/src/styled):
 * Portal does not depend on `ems-common`, and adding that dependency just for
 * this file would gate every change here on publishing a new ems-common
 * version. Keep the two in sync; both go away together once efc is gone.
 *
 * NB: styled-components v5 is CJS with no `exports` map, so pulling the styled
 * function out of it relies on the bundler's `__esModule` interop. That is fine
 * here -- this is project source, always processed by vite -- but it is exactly
 * why the wrapper could not live inside a pre-bundled published package: Node's
 * own ESM loader ignores `__esModule` and hands back `module.exports` instead.
 */
import type * as styledComponents from 'styled-components';
import styledDefault, {
  createGlobalStyle as baseCreateGlobalStyle,
  css as baseCss,
  keyframes as baseKeyframes,
  ThemeProvider as BaseThemeProvider
} from 'styled-components';
import type { EnovaTheme } from 'enova-frontend-components';

const styled = {
  default: styledDefault,
  css: baseCss,
  createGlobalStyle: baseCreateGlobalStyle,
  keyframes: baseKeyframes,
  ThemeProvider: BaseThemeProvider
} as styledComponents.ThemedStyledComponentsModule<EnovaTheme>;

const { css, createGlobalStyle, keyframes, ThemeProvider } = styled;

export { css, createGlobalStyle, keyframes, ThemeProvider };
export type { EnovaTheme };
export default styled.default;
